/**
 * Memory API client.
 *
 * Memory is backed by a git repo on disk (see §4.4.4 + §5). PUT requires
 * If-Match (the HEAD sha) to avoid clobbering concurrent edits.
 */
import { api } from '@/api/client';

export interface MemoryFileInfo {
  filename: string;
  size: number;
  modified_at: string;
  head_sha: string;
}

export interface MemoryListResponse {
  files: MemoryFileInfo[];
  head_sha: string;
}

export interface MemoryFileContent {
  filename: string;
  content: string;
  head_sha: string;
}

// ---------- Backend v2 raw shapes ----------
interface V2MemoryFileMeta {
  name: string;
  size: number;
  modified_at: string;
  etag: string;
}

interface V2MemoryListResponse {
  agent: string;
  files: V2MemoryFileMeta[];
}

interface V2MemoryFileResponse {
  agent: string;
  name: string;
  content: string;
  etag: string;
  updated_at: string;
}

export const memoryApi = {
  async list(agent: string): Promise<MemoryListResponse> {
    const raw = await api<V2MemoryListResponse>(
      `/agents/${encodeURIComponent(agent)}/memory`
    );
    const files: MemoryFileInfo[] = (raw.files ?? []).map((f) => ({
      filename: f.name,
      size: f.size,
      modified_at: f.modified_at,
      head_sha: f.etag,
    }));
    // Backend doesn't track a single HEAD — use the first file etag as a
    // proxy or the empty-tree hash when the agent has no memory yet.
    const head_sha = files[0]?.head_sha ?? 'W/"empty"';
    return { files, head_sha };
  },

  async get(agent: string, filename: string): Promise<MemoryFileContent> {
    const raw = await api<V2MemoryFileResponse>(
      `/agents/${encodeURIComponent(agent)}/memory/${encodeURIComponent(filename)}`
    );
    return {
      filename: raw.name,
      content: raw.content,
      head_sha: raw.etag,
    };
  },

  async put(
    agent: string,
    filename: string,
    content: string,
    headSha: string,
  ): Promise<{ head_sha: string }> {
    const raw = await api<V2MemoryFileResponse>(
      `/agents/${encodeURIComponent(agent)}/memory/${encodeURIComponent(filename)}`,
      {
        method: 'PUT',
        body: { content },
        ifMatch: headSha,
      },
    );
    return { head_sha: raw.etag };
  },

  async remove(agent: string, filename: string): Promise<void> {
    await api<void>(
      `/agents/${encodeURIComponent(agent)}/memory/${encodeURIComponent(filename)}`,
      { method: 'DELETE' },
    );
  },
};

/**
 * Canonical list of memory files a brand-new agent starts with.
 * Tree renders these even if the server doesn't (yet) return them.
 */
export const MEMORY_FILE_TEMPLATES: string[] = [
  'CLAUDE.md',
  'owner_profile.md',
  'rules.md',
  'knowledge.md',
  'preferences.md',
  'MEMORY.md',
];
